import { UTCDate } from "@date-fns/utc";
import { formatISO } from "date-fns/formatISO";
import { HjorteviltJaktdataV1IndividSharedIndividResponse } from "~/src/api/generated/api";
import { Individ, Jaktdag, JaktdagResponse } from "~/src/api/types";
import { AppData } from "~/src/contexts/AppDataContext/DataProvider";
import { AppStorage, StorageItem } from "~/src/contexts/StorageContext/StorageProvider";

export const getFormDataStorageKey = (formStateId?: string) => {
  return "formState-" + location.pathname + (formStateId || "");
};

type HandleOfflineProps = {
  mode: "create" | "update";
  jaktdagToStore: JaktdagResponse;
  appData: AppData;
  storage: AppStorage;
  initialFormData: JaktdagResponse | undefined;
};

export const handleOfflineJaktdagCreateOrUpdate = ({
  mode,
  jaktdagToStore,
  initialFormData,
  appData,
  storage,
}: HandleOfflineProps) => {
  if (mode === "create") {
    jaktdagToStore.RegistrertAv = appData.jegeropplysninger.data?.Navn;

    jaktdagToStore.Individer?.forEach((individ) => {
      individ.Jegernummer = appData.jegeropplysninger.data?.Jegernummer;
      individ.RegistrertAv = appData.jegeropplysninger.data?.Navn;
      individ.RegistrertDato = new Date().toISOString();

      storage.add({
        objectType: "Individ",
        objectKey: { id: individ.Id },
        data: { ...individ, JaktdagId: jaktdagToStore.Id },
        isPending: true,
        operation: "create",
        lastUpdated: new Date(),
      });
    });

    jaktdagToStore.Individer = [];

    storage.add({
      objectType: "Jaktdag",
      objectKey: { id: jaktdagToStore.Id },
      data: jaktdagToStore,
      isPending: true,
      operation: "create",
      lastUpdated: new Date(),
    });
  } else if (mode === "update") {
    const notDelete: (string | undefined)[] = [];
    jaktdagToStore.Individer?.forEach((individ) => {
      notDelete.push(individ.Id);

      if (!individ.Uri) {
        individ.Jegernummer = appData.jegeropplysninger.data?.Jegernummer;
        individ.RegistrertAv = appData.jegeropplysninger.data?.Navn;
        individ.RegistrertDato = new Date().toISOString();

        storage.add({
          objectType: "Individ",
          objectKey: { id: individ.Id },
          data: { ...individ, JaktdagId: jaktdagToStore.Id },
          isPending: true,
          operation: "create",
          lastUpdated: new UTCDate(),
          syncStatus: "pending",
        });
        return;
      }
      // if individ is already created (those without Uri are not created in the api yet),
      // add to local storage to make them available for other functions (setIndividerForJaktdag in StorageProvider)
      if (
        formatISO(initialFormData?.Dato ?? "", { representation: "date" }) !=
          formatISO(jaktdagToStore?.Dato ?? "", { representation: "date" }) ||
        initialFormData?.Jaktfelt != jaktdagToStore.Jaktfelt
      ) {
        if (!storage.get<Individ>({ Id: individ.Id }, "Individ")) {
          storage.add({
            objectType: "Individ",
            objectKey: { id: individ.Id },
            data: individ,
            isPending: true,
            operation: "update",
            lastUpdated: new UTCDate(),
          });
        }
      } else {
        const storedIndivid = storage.get<Individ>({ id: individ.Id }, "Individ");
        storage.add({
          objectType: "Individ",
          objectKey: { id: individ.Id },
          data: storedIndivid?.data ?? individ,
          isPending: storedIndivid?.isPending ?? false,
          operation: storedIndivid?.operation ?? "none",
          syncStatus: storedIndivid?.syncStatus ?? undefined,
        });
      }
    });

    initialFormData?.Individer?.forEach((oldIndivid) => {
      if (oldIndivid.Id && !notDelete.includes(oldIndivid.Id)) {
        storage.add({
          objectType: "Individ",
          objectKey: { id: oldIndivid.Id },
          data: oldIndivid,
          isPending: true,
          operation: "delete",
          lastUpdated: new UTCDate(),
        });
      }
    });
    jaktdagToStore.Individer = [];

    // if create jaktdag is in queue, we update the existing entry and keep operation as create
    const storedJaktdag = storage.get<Jaktdag>({ id: jaktdagToStore.Id }, "Jaktdag");

    storage.add({
      objectType: "Jaktdag",
      objectKey: { id: jaktdagToStore.Id },
      data: jaktdagToStore,
      isPending: true,
      operation: findUpdateMethod(storedJaktdag),
      lastUpdated: new Date(),
    });
  }
};

export const findUpdateMethod = (storedItem: StorageItem<Individ | Jaktdag> | undefined) => {
  if (!storedItem) {
    return "update";
  }
  if (storedItem.syncStatus === "pending") {
    return storedItem.operation;
  }
  return "update"; //for unconfirmed items, we assume they have been succesfuly created already
};

export function checkCanEditIndivider(
  individer: HjorteviltJaktdataV1IndividSharedIndividResponse[],
  changesJaktfeltOrDate?: boolean,
) {
  if (!changesJaktfeltOrDate) return true;
  return !individer.some((i) => i.Godkjent);
}
